import { action, observable, flow } from "mobx";
import { getParcelInfo, getResidents, searchCompanyByNumber, searchActivePersonByInsz } from "services/api";
import TableModel from "./TableModel";
import { APP_LEGGER_TYPES } from "app-constants";

/** Display labels for PatrimoniumPercelen column keys (API field → UI header). */
const PATRIMONY_PARCEL_COLUMN_LABELS = {
  Belastb_opp: "Belastb. opp",
  Niet_belastb_opp: "Niet-belastb. opp",
  Polders_of_watering: "Polders of watering",
};

export default class PerceelInfoModel {
  uiStore;

  @observable
  searchValue = "";

  @observable
  loading = false;

  @observable
  capakey = {};

  @observable
  polygon = {};

  @observable
  periodData = [];

  @observable
  activePeriod = "";

  @observable
  patrimonyParcels = new TableModel();

  @observable
  owners = new TableModel();

  @observable
  residents = new TableModel();

  @observable
  vkbo = new TableModel();

  @observable
  personalInfo = new TableModel();

  @observable
  isPersonDeceased = false;

  @observable
  deceasedPersonInsz = null;

  @observable
  deceasedOwnerInszList = [];

  @observable
  leggerType;

  @observable
  houseHolder = false;

  @action.bound
  loadParcelInfo = flow(function* (capakey, leggerType) {
    this.capakey = typeof capakey === "object" ? capakey : { full: capakey };
    this.loading = true;
    this.leggerType = leggerType;
    this.patrimonyParcels.insert([]);
    this.owners.insert([]);
    this.resetOtherTabs();
    try {
      const { data: periodData } = yield getParcelInfo(this.capakey.full, this.leggerType);

      this.periodData = periodData;

      // check if period contains the word "ongoing"
      this.setActivePeriod(periodData.find((pData) => pData.period.includes("ongoing")).period);

      this.loading = false;
      return true;
    } catch (error) {
      this.loading = false;
      return false;
    }
  });

  @action.bound
  getDataForPeriod = flow(function* (period) {
    if (period !== "" && this.periodData.length > 0) {
      this.loading = true;
      this.patrimonyParcels = new TableModel();
      this.owners = new TableModel();
      this.patrimonyParcels.insert([]);
      this.owners.insert([]);

      const { PatrimoniumPercelen, Eigenaars } = this.periodData.find((pData) => pData.period === period);

      PatrimoniumPercelen.sort((a, b) => (a.Partitie > b.Partitie ? 1 : -1));
      Eigenaars.sort((a, b) => (a.Partitie > b.Partitie ? 1 : -1));

      this.patrimonyParcels.insert(PatrimoniumPercelen, undefined, PATRIMONY_PARCEL_COLUMN_LABELS);
      this.owners.insert(Eigenaars);
      this.deceasedOwnerInszList = this.periodData.find((pData) => pData.period === period)?.DeceasedOwnerInsz ?? [];

      this.loading = false;
    }
  });

  @action.bound
  loadResidents = flow(function* (capakey, shape, houseHolder, busNumberFilter) {
    this.loading = true;
    const { data } = yield getResidents(this.capakey.full, shape, houseHolder, busNumberFilter);
    this.residents.insert(data);
    this.loading = false;
  });

  @action.bound
  loadVkbo = flow(function* (type, insz) {
    this.loading = true;
    this.vkbo.insert([]);
    const { data } = yield searchCompanyByNumber(APP_LEGGER_TYPES.ACTUAL, insz);

    if (data && data[0]) {
      const {
        Ondernemingsnummer,
        MaatschappelijkeNaam,
        description: { Huisnummer, Busnummer, Descripties: { Descriptie }, Gemeente: { PostCode } },
      } = data[0];

      const Adres = Descriptie[0].Adres;
      const Contact = Descriptie[0].Contact;
      const Straat = Adres.Straat.Naam;
      const Gemeente = Adres.Gemeente.Naam;

      const companyInfo = {
        MaatschappelijkeNaam,
        Ondernemingsnummer,
        Straat,
        Huisnummer,
        PostCode,
        Gemeente,
        Busnummer: Busnummer ? Busnummer : "",
        Telefoonnummer: Contact && Contact.Telefoonnummer,
        Email: Contact && Contact.Email,
        Website: Contact && Contact.Website,
      };

      this.vkbo.insert([companyInfo]);
      this.loading = false;
    }
  });

  @action.bound
  loadPersonalInfo = flow(function* (insz) {
    this.loading = true;

    const { data } = yield searchActivePersonByInsz(insz);

    if (data && data[0]) {
      const { overleden, overlijdensdatum, ...displayData } = data[0];
      this.isPersonDeceased = !!overleden;
      this.deceasedPersonInsz = !!overleden ? insz : null;
      const insertData = [
        {
          ...displayData,
          ...(overlijdensdatum ? { Overlijdensdatum: overlijdensdatum } : {}),
        },
      ];
      this.personalInfo.insert(insertData);
      this.loading = false;
    }
  });

  resetOtherTabs = () => {
    this.residents.insert([]);
    this.personalInfo.insert([]);
    this.vkbo.insert([]);
    this.isPersonDeceased = false;
    this.deceasedPersonInsz = null;
    this.deceasedOwnerInszList = [];
  };

  @action
  reset = () => {
    this.capakey = {};
    this.polygon = {};
    this.loading = false;
    this.periodData = [];
    this.activePeriod = "";
    this.leggerType = undefined;
    this.patrimonyParcels.insert([]);
    this.owners.insert([]);
    this.resetOtherTabs();
  };

  @action
  setActivePeriod = (period) => {
    this.activePeriod = period;
    this.getDataForPeriod(period);
  };

  @action
  searchParcelInfo = (e) => {
    this.searchValue = e.target.value;
  };

  @action
  setCapakey = (value) => {
    console.log("setCapaKEY", value);
    this.capakey = value;
  };

  @action
  setLoading = (value) => {
    console.log("set loading to false");
    this.loading = value;
  };
}
